/**
 * What the console will actually navigate to today.
 *
 * `NAV` is the designed information architecture — 117 screens, transcribed
 * from the prototype and extended only by recorded decisions. This module is a
 * different question asked of the same data: **which of those screens read the
 * backend**. The answer is derived from `SCREEN_LOADERS` and from nothing else,
 * so there is no second list to keep in step.
 *
 * # Why unbuilt screens are hidden rather than badged
 *
 * A screen with no loader renders the design corpus: fixture rows transcribed
 * from the prototype, carrying a `corpus` chip in the header. That was honest
 * when the console had thirteen live screens and was plainly a scaffold — the
 * chip was the exception and the scaffold was the rule.
 *
 * It stops being honest as the live count grows. The corpus does not render a
 * placeholder; it renders realistic operational data — case counts, balances,
 * queue depths, customer rows. Once a meaningful fraction of what an operator
 * opens IS real, the chip is asking them to re-read a header every time to know
 * whether the table under it exists. The failure mode is silent and it is
 * worse than a missing menu entry: nobody is misled by a screen they cannot
 * reach.
 *
 * The rail already accepted this argument in the narrow case. #30 deleted the
 * pinned counts and the sixteen per-screen badges because "the console could
 * show `Live · postgres` over real figures and an invented `11` beside them,
 * and an operator who has learned that this console distinguishes the two would
 * reasonably read the rail as real". Those were single numbers in chrome. This
 * is whole tables in the content area.
 *
 * # This is not law 18, and the distinction is the whole justification
 *
 * Law 18 forbids hiding permission-gated controls: they are shown disabled with
 * the required role named, "so staff know an escalation path exists". That
 * reasoning is about AUTHORITY — somebody can grant it, and an operator who
 * knows the control exists knows who to ask.
 *
 * An unbuilt screen has no escalation path, no role that unlocks it and nothing
 * an operator can do with the knowledge that it was designed. Hiding a
 * capability somebody could be granted and hiding one that does not exist are
 * different acts; law 18 forbids the first.
 *
 * # NAV is not edited, and must not be
 *
 * The design contract stays whole. Shrinking `NAV` to match what is built would
 * lose the specification each unbuilt screen is measured against, and would
 * make "what is left to build" unanswerable from this repository. Reachability
 * is a fact about today; the IA is a fact about the product.
 */

import { NAV, PINS, labelOf, sectionOf, type NavItem, type NavSection } from './nav'
import type { NoteBlock } from './spec/types'
import { SCREEN_LOADERS } from '@/lib/data'

/**
 * Whether a screen reads the backend.
 *
 * The registry is the single source of truth for this, and it is already the
 * one thing a reviewer reads to answer "is this screen real". Deriving from it
 * means wiring a loader is the ONLY action needed to reveal a screen — there is
 * no second place to remember.
 */
export function isReachable(screenId: string): boolean {
  return Object.hasOwn(SCREEN_LOADERS, screenId)
}

/**
 * The rail's sections, filtered to screens that read the backend.
 *
 * A section whose every screen is unwired is dropped entirely rather than
 * rendered empty: a collapsible header that opens onto nothing is a worse
 * answer than no header, and it invites the same "is this broken?" question the
 * corpus tables do.
 */
export const REACHABLE_NAV: readonly NavSection[] = NAV
  .map(([label, tone, items]): NavSection => [label, tone, items.filter((i: NavItem) => isReachable(i[0]))])
  .filter(([, , items]) => items.length > 0)

/**
 * A section's position in the DESIGNED `NAV`, for `SEC_ICONS`.
 *
 * `SEC_ICONS` is a positional array, and filtering sections out of the rail
 * shifts every index after the first gap — so the rail would silently draw the
 * wrong glyph on most sections. That is the kind of bug that looks like a
 * design regression rather than a logic error, so the lookup is by label and
 * against the unfiltered IA, and this comment is why.
 */
export function iconIndexOf(sectionLabel: string): number {
  return NAV.findIndex(([label]) => label === sectionLabel)
}

/**
 * The pinned shortcuts, filtered the same way.
 *
 * Pins are the five things a shift starts from, so an unbuilt one is the most
 * costly kind: it is the first thing clicked, by somebody who has not yet
 * looked at anything real today and has no live screen beside it for contrast.
 */
export const REACHABLE_PINS = PINS.filter(([id]) => isReachable(id))

/**
 * Every screen the console will navigate to, in rail order.
 *
 * For the palette, and for the tests that assert the rail is a subset of the
 * registry.
 */
export function reachableScreenIds(): string[] {
  return REACHABLE_NAV.flatMap(([, , items]) => items.map((i) => i[0]))
}

/**
 * The one-line reason, stated once so the notice and the provenance agree.
 */
export function notBuiltReason(screenId: string): string {
  return `${labelOf(screenId)} is designed and not yet connected to the backend.`
}

/**
 * What an unbuilt screen renders.
 *
 * # It is a notice, not an empty state and not an error
 *
 * Three different claims, and the console already distinguishes the other two:
 * `EmptyView` says the backend answered and had nothing, `ErrorView` says the
 * read failed. This says nobody has built it — the third state in the
 * `available` / `not_built` / `unavailable` vocabulary, and the one that had no
 * rendering because it used to be dressed as fixture data instead.
 *
 * Tone `n`, deliberately. `r` would put a red notice on every unbuilt screen
 * and teach an operator that red means "not my problem", which is the exact
 * habit law 20 exists to prevent.
 *
 * # It names the section, because arriving here means a link went stale
 *
 * These screens are not in the rail, so the only ways here are a bookmark, a
 * pasted URL or a hand-edited path. Whoever did that had a destination in mind;
 * naming what the screen was designed to be, and where it sits, is what lets
 * them decide whether to wait for it or ask for it.
 */
export function notBuiltBlocks(screenId: string): readonly NoteBlock[] {
  return [{
    t: 'note',
    tone: 'n',
    kicker: sectionOf(screenId),
    title: `${labelOf(screenId)} is not built yet`,
    body:
      'This screen is part of the designed console and has no backend read behind it, so there '
      + 'is nothing to show. It is deliberately blank rather than filled with example data: a '
      + 'table of plausible figures that nobody read is worse than an empty screen, because it '
      + 'looks like an answer. It will appear in the sidebar as soon as it is connected.',
    ref: `screen ${screenId}`,
  }]
}

/**
 * Screens that are designed and not yet connected.
 *
 * Exported for the tests and for anything that wants to report progress
 * honestly. Deliberately NOT rendered as a list anywhere in the console: "here
 * are 70 things you cannot use" is not information an operator can act on, and
 * the roadmap lives in the issue tracker where it can carry reasons.
 */
export function unreachableScreenIds(): string[] {
  return NAV.flatMap(([, , items]) => items.filter((i) => !isReachable(i[0])).map((i) => i[0]))
}
